import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Sheet } from 'components/ui/sheet'
import { Button } from 'components/ui/button'
import ProjectModifyPopup from 'components/popups/ProjectModifyPopup'
import { Trash2, GitBranch } from 'lucide-react'
import { deleteProject, initializeMyProjects, updateProject } from 'slices/projectSlice'

interface ProjectActionSheetProps {
	project: any
	open: boolean
	onClose: () => void
	onEdit?: (values: any) => Promise<void>
	onDelete?: () => Promise<void>
	showDelete?: boolean
}

const ProjectActionSheet = ({
	project,
	open,
	onClose,
	onEdit,
	onDelete,
	showDelete = true,
}: ProjectActionSheetProps) => {
	const dispatch = useDispatch()

	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

	// Reset confirmation state when sheet opens/closes or project changes
	useEffect(() => {
		setShowDeleteConfirm(false)
	}, [project, open])

	if (!project) {
		return (
			<Sheet open={false} onClose={onClose} side="right" className="!w-[500px]">
				<div />
			</Sheet>
		)
	}

	const handleEditProject = async (values: any) => {
		if (onEdit) {
			await onEdit(values)
			onClose()
			return
		}
		const updatedProject = { ...values, updateNote: 'Edited Project.' }
		// @ts-ignore
		await dispatch(updateProject(updatedProject))
		// @ts-ignore
		await dispatch(initializeMyProjects())
		onClose()
	}

	const handleDelete = async () => {
		if (onDelete) {
			await onDelete()
			onClose()
			return
		}
		// @ts-ignore
		await dispatch(deleteProject({ projectId: project._id }))
		// @ts-ignore
		await dispatch(initializeMyProjects())
		onClose()
	}

	return (
		<Sheet open={open} onClose={onClose} side="right" className="!w-[500px]">
			{/* Header */}
			<div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200">
				<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe] shrink-0">
					<GitBranch className="w-5 h-5" />
				</div>
				<div className="min-w-0 flex-1">
					<h2 className="text-lg font-semibold text-gray-900 truncate">{project.projectName}</h2>
					<p className="text-sm text-gray-500">Edit project details and settings</p>
				</div>
			</div>

			{/* Body — Edit Form */}
			<div className="flex-1 overflow-y-auto p-5">
				<ProjectModifyPopup editProject={handleEditProject} recordForEdit={project} />
			</div>

			{/* Footer — Delete */}
			{showDelete && (
				<div className="px-5 py-4 border-t border-gray-200 bg-gray-50">
					{showDeleteConfirm ? (
						<div className="flex items-center gap-2">
							<span className="text-sm text-red-600">Delete this project?</span>
							<Button variant="danger" onClick={handleDelete}>
								Yes, Delete
							</Button>
							<Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
								Cancel
							</Button>
						</div>
					) : (
						<Button variant="ghost-red" onClick={() => setShowDeleteConfirm(true)}>
							<Trash2 className="w-4 h-4 mr-1" />
							Delete
						</Button>
					)}
				</div>
			)}
		</Sheet>
	)
}

export default ProjectActionSheet
